/* prettier-ignore-file */
/**
 * Wizard step 4 — customer details.
 *
 * NewBooking.js only checks these fields for non-emptiness (NewBooking.js:4698-4790)
 * — there is no format validation anywhere, so typo'd emails and short phone
 * numbers get saved and receipts silently fail. This step keeps the same required
 * fields but adds basic shape checks: a 10-digit phone and an email containing a
 * user, an @ and a dotted domain.
 */

import React from "react";
import { View, Text, StyleSheet, ScrollView, TextInput } from "react-native";
import { formatPhoneNumberProgressive, capitalizeFirstLetter } from "../../utils/formatting";

export const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || "").trim());
export const isValidPhone = (phone) => (phone || "").replace(/\D/g, "").length === 10;

const StepCustomerInfo = ({ firstName, lastName, customerEmail, customerPhone, onChange }) => {
  const emailTouched = (customerEmail || "").length > 0;
  const phoneTouched = (customerPhone || "").length > 0;
  const emailBad = emailTouched && !isValidEmail(customerEmail);
  const phoneBad = phoneTouched && !isValidPhone(customerPhone);

  return (
    <ScrollView style={styles.container}>
      <View style={styles.section}>
        <Text style={styles.sectionTitle}>Your details</Text>

        <View style={styles.nameRow}>
          <View style={styles.nameField}>
            <Text style={styles.fieldLabel}>First name</Text>
            <TextInput
              style={styles.input}
              value={firstName}
              onChangeText={(text) => onChange({ firstName: capitalizeFirstLetter(text) })}
              placeholder="First name"
              placeholderTextColor="#AEB8B6"
              autoCapitalize="sentences"
            />
          </View>
          <View style={styles.nameField}>
            <Text style={styles.fieldLabel}>Last name</Text>
            <TextInput
              style={styles.input}
              value={lastName}
              onChangeText={(text) => onChange({ lastName: capitalizeFirstLetter(text) })}
              placeholder="Last name"
              placeholderTextColor="#AEB8B6"
              autoCapitalize="sentences"
            />
          </View>
        </View>

        <Text style={styles.fieldLabel}>Email</Text>
        <TextInput
          style={[styles.input, emailBad && styles.inputError]}
          value={customerEmail}
          onChangeText={(text) => onChange({ customerEmail: text })}
          placeholder="you@example.com"
          placeholderTextColor="#AEB8B6"
          autoCapitalize="none"
          keyboardType="email-address"
        />
        {emailBad && <Text style={styles.errorText}>That doesn't look like a valid email address.</Text>}
        <Text style={styles.hintText}>Your receipt and booking confirmation go here.</Text>

        <Text style={styles.fieldLabel}>Phone</Text>
        <TextInput
          style={[styles.input, phoneBad && styles.inputError]}
          value={customerPhone}
          onChangeText={(text) => onChange({ customerPhone: formatPhoneNumberProgressive(text) })}
          placeholder="(555) 555-5555"
          placeholderTextColor="#AEB8B6"
          keyboardType="phone-pad"
        />
        {phoneBad && <Text style={styles.errorText}>Enter a 10-digit phone number.</Text>}
        <Text style={styles.hintText}>We text delivery updates and your waiver link here.</Text>
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1 },
  section: { backgroundColor: "#FFFFFF", borderRadius: 8, padding: 16, marginBottom: 12 },
  sectionTitle: { fontSize: 16, fontWeight: "bold", color: "#2E3A38", marginBottom: 12 },

  nameRow: { flexDirection: "row", justifyContent: "space-between" },
  nameField: { flex: 1, marginRight: 8 },

  fieldLabel: { fontSize: 12, color: "#5C6B68", marginBottom: 4, marginTop: 12 },
  input: {
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    padding: 12,
    fontSize: 16,
    backgroundColor: "#FFFFFF",
  },
  inputError: { borderColor: "#C4453D" },
  errorText: { fontSize: 12, color: "#C4453D", marginTop: 4 },
  hintText: { fontSize: 11, color: "#8A9794", marginTop: 4 },
});

export default StepCustomerInfo;
